import { Autocomplete } from "@mui/lab";
import { Box, TextField } from "@mui/material";
import { FieldError } from "react-hook-form";
import { EventTypeTag } from "../components/EventTypeTag";

export const productionTypes = [
	{
		code: "P",
		name: "Planned",
		borderColor: "#F59E0B",
		backgroundColor: "#FEF3C7",
		textColor: "#92400E",
	},
	{
		code: "C",
		name: "Completed",
		borderColor: "#10B981",
		backgroundColor: "#D1FAE5",
		textColor: "#065F46",
	},
	{
		code: "K",
		name: "Confirmed",
		borderColor: "#8B5CF6",
		backgroundColor: "#EDE9FE",
		textColor: "#5B21B6",
	},
	{
		code: "I",
		name: "In Progress",
		borderColor: "#3B82F6",
		backgroundColor: "#DBEAFE",
		textColor: "#1E3A8A",
	},
];

export const eventTypes = [
	{
		code: "P",
		name: "Production",
		abbreviatedName: "Prod.",
		borderColor: "#3B82F6",
		backgroundColor: "#DBEAFE",
		textColor: "#1E40AF",
	},
	{
		code: "M",
		name: "Maintenance",
		abbreviatedName: "Maint.",
		borderColor: "#FDBA74",
		backgroundColor: "#FFF7ED",
		textColor: "#F97316",
	},
	{
		code: "D",
		name: "Downtime",
		abbreviatedName: "Down.",
		borderColor: "#67E8F9",
		backgroundColor: "#CFFAFE",
		textColor: "#0E7490",
	},
	{
		code: "H",
		name: "Holiday",
		abbreviatedName: "Holiday",
		borderColor: "#D0A3FF",
		backgroundColor: "#EDE7F6",
		textColor: "#4A148C",
	},
	{
		code: "O",
		name: "Other",
		abbreviatedName: "Other",
		borderColor: "#6B7280",
		backgroundColor: "#F3F4F6",
		textColor: "#374151",
	},
];

type Props = {
	label?: string;
	value: string | null;
	onChange?: Function;
	readOnly?: boolean;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
};

export const EventTypeSelector = ({
	label = "Type",
	value,
	readOnly = false,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
}: Props) => {
	const selectedType =
		eventTypes.find((code) => code.code === value) || eventTypes[0];

	return (
		<Autocomplete
			id="event-type-selector"
			clearOnEscape
			autoHighlight
			disableClearable
			options={eventTypes}
			getOptionLabel={(option) => option.name}
			renderOption={(props, option) => (
				<Box
					{...props}
					component="li"
					sx={{
						display: "flex",
						alignItems: "center",
					}}
				>
					<span
						style={{
							width: "25px",
							height: "25px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							marginRight: "8px",
							marginLeft: "8px",
						}}
					>
						<EventTypeTag
							event={{
								Code: option.code,
								Name: option.name,
							}}
						/>
					</span>
					{option.name}
				</Box>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					required={required}
					label={label}
					aria-readonly={readOnly}
					fullWidth
					error={!!fieldError}
					helperText={fieldError?.message}
					size="small"
					margin="none"
					inputProps={{
						...params.inputProps,
						"data-test-id": "event-type-selector",
					}}
					InputProps={{
						...params.InputProps,
						startAdornment: selectedType ? (
							<span
								style={{
									width: "25px",
									height: "25px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									marginRight: "8px",
									marginLeft: "8px",
								}}
							>
								<EventTypeTag
									event={{
										Code: selectedType?.code ?? "",
										Name: selectedType?.name ?? "",
									}}
								/>
							</span>
						) : null,
					}}
				/>
			)}
			value={selectedType}
			onChange={(event, newValue) => {
				onChange(newValue?.code ?? null);
			}}
			disabled={disabled}
		/>
	);
};
